import { api, extractApiErrorMessage } from './api';

// Types
export interface YogaSchool {
  _id: string;
  name: string;
  slug: string;
  description: string;
  location: string;
  country: string;
  city: string;
  phone?: string;
  email?: string;
  website?: string;
  rating: number;
  totalReviews: number;
  price?: number;
  currency: string;
  duration?: number;
  maxStudents?: number;
  isActive: boolean;
  isFeatured: boolean;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED';
  verificationDocuments?: Array<string | { url: string; type: string }>;
  verificationMeta?: Record<string, string>;
  ownerId?: string | { _id: string; firstName?: string; lastName?: string; email?: string };
  images?: string[];
  amenities?: string[];
  programs?: string[];
  instructor?: {
    name: string;
    experience: number;
    certifications: string[];
  };
  createdAt: string;
  updatedAt: string;
  /** Who created the school listing (admin onboard vs owner self-registration) */
  createdBySource?: 'YOGA_SCHOOL' | 'BOOKMYYOGATTC';
  _isUpdating?: boolean; // For UI loading states
}

export interface YogaSchoolStats {
  totalSchools: number;
  activeSchools: number;
  featuredSchools: number;
  pendingSchools: number;
  averageRating: number;
  totalReviews: number;
  averagePrice: number;
  countryCount: number;
}

export interface PaginationInfo {
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  /** API may return pages/total instead of totalPages/totalItems */
  pages?: number;
  total?: number;
}

export interface GetYogaSchoolsParams {
  page?: number;
  limit?: number;
  isActive?: string;
  isFeatured?: string;
  search?: string;
  location?: string;
  priceMin?: number;
  priceMax?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Admin list: filter by how the school record was created */
  createdBySource?: 'YOGA_SCHOOL' | 'BOOKMYYOGATTC';
  /** Pending registrations: filter owners who submitted verification uploads */
  verificationDocumentsSubmitted?: 'true' | 'false';
}

export interface GetYogaSchoolsResponse {
  schools: YogaSchool[];
  pagination: PaginationInfo;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Yoga School Service Class
export class YogaSchoolService {
  private static instance: YogaSchoolService;

  public static getInstance(): YogaSchoolService {
    if (!YogaSchoolService.instance) {
      YogaSchoolService.instance = new YogaSchoolService();
    }
    return YogaSchoolService.instance;
  }

  // Get yoga schools with pagination and filters
  async getYogaSchools(params: GetYogaSchoolsParams = {}): Promise<ApiResponse<GetYogaSchoolsResponse>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.isActive) queryParams.append('isActive', params.isActive);
      if (params.isFeatured) queryParams.append('featured', params.isFeatured);
      if (params.search) queryParams.append('search', params.search);
      if (params.location) queryParams.append('location', params.location);
      if (params.priceMin) queryParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax) queryParams.append('priceMax', params.priceMax.toString());
      if (params.sortBy) queryParams.append('sort', params.sortBy);
      if (params.sortOrder) queryParams.append('order', params.sortOrder);
      if (params.createdBySource) queryParams.append('createdBySource', params.createdBySource);

      const url = `/schools?${queryParams.toString()}`;
      console.log('Admin API call URL:', url);
      console.log('Admin query params:', queryParams.toString());
      
      // Note: Admin uses GET /api/v3/schools (different endpoint from public /api/schools)
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga schools:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga schools"));
    }
  }

  // Get yoga school by ID
  async getYogaSchoolById(id: string): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.get(`/schools/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga school"));
    }
  }

  // Get yoga school by slug
  async getYogaSchoolBySlug(slug: string): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.get(`/schools/${slug}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga school"));
    }
  }

  // Create yoga school
  async createYogaSchool(schoolData: Partial<YogaSchool>): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.post('/schools', schoolData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to create yoga school"));
    }
  }

  /**
   * Admin-driven onboarding: creates a YOGA_SCHOOL user + a pre-approved
   * school in one request. No email OTP. Caller must be SUPER_ADMIN or
   * SUPPORT_ADMIN (enforced server-side).
   */
  async onboardSchool(payload: {
    owner: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      phone?: string;
    };
    school: Record<string, any>;
  }): Promise<ApiResponse<{ user: any; school: YogaSchool }>> {
    try {
      const response = await api.post('/schools/onboard', payload);
      return response.data;
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      console.error('Error onboarding school:', error);
      throw new Error(apiMsg || 'Failed to onboard school');
    }
  }

  // Update yoga school
  async updateYogaSchool(id: string, schoolData: Partial<YogaSchool>): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.put(`/schools/${id}`, schoolData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to update yoga school"));
    }
  }

  // Delete yoga school
  async deleteYogaSchool(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/schools/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to delete yoga school"));
    }
  }

  // Toggle yoga school status (activate/deactivate)
  async toggleYogaSchoolStatus(id: string, isActive: boolean): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.put(`/schools/${id}/toggle-status`, { isActive });
      return response.data;
    } catch (error: any) {
      console.error('Error toggling yoga school status:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to toggle yoga school status"));
    }
  }

  // Toggle featured status
  async toggleFeaturedStatus(id: string): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.post(`/schools/${id}/feature`);
      return response.data;
    } catch (error: any) {
      console.error('Error toggling featured status:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to toggle featured status"));
    }
  }

  // Verify yoga school
  async verifyYogaSchool(id: string): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.post(`/schools/${id}/verify`);
      return response.data;
    } catch (error: any) {
      console.error('Error verifying yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to verify yoga school"));
    }
  }

  // Get pending yoga schools
  async getPendingYogaSchools(params: Omit<GetYogaSchoolsParams, 'isActive'> = {}): Promise<ApiResponse<GetYogaSchoolsResponse>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sort', params.sortBy);
      if (params.sortOrder) queryParams.append('order', params.sortOrder);
      if (params.createdBySource) queryParams.append('createdBySource', params.createdBySource);
      if (params.verificationDocumentsSubmitted) {
        queryParams.append('verificationDocumentsSubmitted', params.verificationDocumentsSubmitted);
      }

      const response = await api.get(`/schools/pending?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching pending yoga schools:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch pending yoga schools"));
    }
  }

  // Approve a yoga school
  async approveSchool(id: string): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.post(`/schools/${id}/approve`);
      return response.data;
    } catch (error: any) {
      console.error('Error approving yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to approve yoga school"));
    }
  }

  // Reject a yoga school
  async rejectSchool(id: string, reason?: string): Promise<ApiResponse<YogaSchool>> {
    try {
      const response = await api.post(`/schools/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      console.error('Error rejecting yoga school:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to reject yoga school"));
    }
  }

  // Get featured yoga schools
  async getFeaturedYogaSchools(limit?: number): Promise<ApiResponse<{ schools: YogaSchool[] }>> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());

      const response = await api.get(`/schools/featured?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching featured yoga schools:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch featured yoga schools"));
    }
  }

  // Search yoga schools
  async searchYogaSchools(searchTerm: string, params: Omit<GetYogaSchoolsParams, 'search'> = {}): Promise<ApiResponse<GetYogaSchoolsResponse>> {
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('q', searchTerm);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.isActive) queryParams.append('isActive', params.isActive);
      if (params.isFeatured) queryParams.append('featured', params.isFeatured);
      if (params.location) queryParams.append('location', params.location);
      if (params.priceMin) queryParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax) queryParams.append('priceMax', params.priceMax.toString());
      if (params.sortBy) queryParams.append('sort', params.sortBy);
      if (params.sortOrder) queryParams.append('order', params.sortOrder);

      const response = await api.get(`/schools/search?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error searching yoga schools:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to search yoga schools"));
    }
  }

  // Get yoga schools by location
  async getYogaSchoolsByLocation(location: string, params: Omit<GetYogaSchoolsParams, 'location'> = {}): Promise<ApiResponse<GetYogaSchoolsResponse>> {
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('location', location);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.isActive) queryParams.append('isActive', params.isActive);
      if (params.isFeatured) queryParams.append('featured', params.isFeatured);
      if (params.search) queryParams.append('search', params.search);
      if (params.priceMin) queryParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax) queryParams.append('priceMax', params.priceMax.toString());
      if (params.sortBy) queryParams.append('sort', params.sortBy);
      if (params.sortOrder) queryParams.append('order', params.sortOrder);

      const response = await api.get(`/schools?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga schools by location:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga schools by location"));
    }
  }

  // Get yoga school statistics
  async getYogaSchoolStats(): Promise<ApiResponse<{ stats: YogaSchoolStats }>> {
    try {
      const response = await api.get('/schools/stats');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga school stats:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga school statistics"));
    }
  }

  // Get yoga school programs
  async getYogaSchoolPrograms(id: string): Promise<ApiResponse<{ programs: any[] }>> {
    try {
      const response = await api.get(`/schools/${id}/programs`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga school programs:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga school programs"));
    }
  }

  // Get yoga school reviews
  async getYogaSchoolReviews(id: string, params: { page?: number; limit?: number } = {}): Promise<ApiResponse<{ reviews: any[]; pagination: PaginationInfo }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const response = await api.get(`/schools/${id}/reviews?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching yoga school reviews:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to fetch yoga school reviews"));
    }
  }

  // Create yoga school review
  async createYogaSchoolReview(id: string, reviewData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/schools/${id}/reviews`, reviewData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating yoga school review:', error);
      throw new Error(extractApiErrorMessage(error, "Failed to create yoga school review"));
    }
  }
}

// Export singleton instance
export const yogaSchoolService = YogaSchoolService.getInstance();

// Utility functions
export const formatYogaSchoolStatus = (isActive: boolean): string => {
  return isActive ? 'Active' : 'Inactive';
};

export const formatFeaturedStatus = (isFeatured: boolean): string => {
  return isFeatured ? 'Featured' : 'Regular';
};

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatDuration = (days: number): string => {
  if (days < 30) {
    return `${days} days`;
  } else if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(days / 365);
    return `${years} year${years > 1 ? 's' : ''}`;
  }
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return formatDate(dateString);
};

export const getRatingColor = (rating: number): string => {
  if (rating >= 4.5) return 'text-green-600';
  if (rating >= 4.0) return 'text-yellow-600';
  if (rating >= 3.0) return 'text-orange-600';
  return 'text-red-600';
};

export const getStatusBadgeColor = (isActive: boolean): string => {
  return isActive 
    ? 'bg-green-100 text-green-800' 
    : 'bg-red-100 text-red-800';
};

export const getFeaturedBadgeColor = (isFeatured: boolean): string => {
  return isFeatured 
    ? 'bg-yellow-100 text-yellow-800' 
    : 'bg-gray-100 text-gray-800';
};
